import { useState } from "react"
import type { CreativeForecastJobResult } from "~/ui-rtk/api/commentModerationApi"
import MarathonImage from "~/ui-rtk/components/atoms/MarathonImage/MarathonImage"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import Button from "~/ui-rtk/components/ui/controls/Button"
import {
  Legend,
  QuoteList,
  ScoreBadge,
  StanceBar,
  aspectLabel,
  stanceTitle,
} from "~/ui-rtk/pages/CommentModeration/reactions"

const DIALOG_CLASSES = {
  container:
    "bg-basic-black border border-system-blue-600/40 p-6 rounded-xl text-left text-white",
}

const CONFIDENCE_CLS = {
  low: "bg-system-gray-500/20 text-system-gray-300",
  medium: "bg-sky-500/20 text-sky-300",
  high: "bg-emerald-500/20 text-emerald-300",
} as const

const MATCH_BADGE = {
  same: { label: "Same creative", cls: "bg-emerald-500/20 text-emerald-300" },
  similar: { label: "Similar style", cls: "bg-sky-500/20 text-sky-300" },
  different: {
    label: "Different — not counted",
    cls: "bg-rose-500/20 text-rose-300",
  },
} as const

const STANCE_LABEL = {
  POSITIVE: "mostly positive",
  NEGATIVE: "mostly pushback",
  QUESTION: "mostly questions",
  NEUTRAL: "mixed",
} as const

function pct(share: number): string {
  return `${Math.round(share * 100)}%`
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[12px] uppercase tracking-wide text-system-gray-500">
      {children}
    </div>
  )
}

/** The whole forecast: the total audience report first, then every similar creative one by one. */
export default function CreativeForecastDialog({
  forecast,
  isOpen,
  onClose,
}: {
  forecast: CreativeForecastJobResult
  isOpen: boolean
  onClose: () => void
}) {
  const [index, setIndex] = useState(0)
  const evidence = forecast.evidence
  const current = evidence[Math.min(index, Math.max(0, evidence.length - 1))]
  const expectedCounts = {
    positive: Math.round(forecast.expected.positive * 100),
    negative: Math.round(forecast.expected.negative * 100),
    question: Math.round(forecast.expected.question * 100),
    neutral: Math.round(forecast.expected.neutral * 100),
  }

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={onClose}
      width={900}
      classes={DIALOG_CLASSES}
      closeOnClickOutside
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Audience forecast</h3>
          <p className="text-[14px] text-system-gray-400">
            Built from {forecast.similarWithComments} similar creative
            {forecast.similarWithComments === 1 ? "" : "s"} you already ran and
            the {forecast.commentsConsidered} comments their audiences left.
            {forecast.similarFound > forecast.similarWithComments
              ? ` ${forecast.similarFound - forecast.similarWithComments} more looked similar but had no judged comments.`
              : ""}
            {forecast.rejectedSimilar
              ? ` AI checked the matches and set ${forecast.rejectedSimilar} aside as a different style.`
              : ""}
          </p>
        </div>
        <Button size="sm" variant={{ variant: "text" }} onClick={onClose}>
          Close
        </Button>
      </div>

      <div className="mt-4 flex max-h-[70vh] flex-col gap-system-md overflow-y-auto pr-1">
        {/* Total audience report */}
        <section className="flex flex-col gap-system-sm rounded-lg border border-white/10 bg-white/5 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-[15px] font-medium">Total audience report</h4>
            <Legend />
          </div>
          {forecast.ai && (
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 text-[12px] ${CONFIDENCE_CLS[forecast.ai.confidence]}`}
                >
                  {forecast.ai.confidence} confidence
                </span>
                <span className="text-[12px] text-system-gray-500">
                  expected reception: {STANCE_LABEL[forecast.ai.expected]}
                </span>
              </div>
              <p className="text-[14px] text-system-gray-100">
                {forecast.ai.headline}
              </p>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <StanceBar
                counts={expectedCounts}
                title={`Expected: ${pct(forecast.expected.positive)} positive · ${pct(forecast.expected.question)} questions · ${pct(forecast.expected.neutral)} neutral · ${pct(forecast.expected.negative)} negative`}
              />
            </div>
            <ScoreBadge score={forecast.expectedScore} />
          </div>
          <div className="grid grid-cols-1 gap-system-sm md:grid-cols-2">
            {forecast.ai?.watchOuts.length ? (
              <div>
                <SectionTitle>Watch out for</SectionTitle>
                <ul className="mt-1 list-inside list-disc text-[14px] text-system-gray-200">
                  {forecast.ai.watchOuts.map(w => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {forecast.objections.length > 0 && (
              <div>
                <SectionTitle>Pushback that recurred</SectionTitle>
                <ul className="mt-1 flex flex-col gap-0.5 text-[14px] text-system-gray-200">
                  {forecast.objections.map(o => (
                    <li key={o.claim}>
                      <span className="font-mono text-system-gray-500">
                        {o.mentions}×
                      </span>{" "}
                      {o.claim}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {forecast.questions.length > 0 && (
              <div>
                <SectionTitle>Questions to expect</SectionTitle>
                <ul className="mt-1 list-inside list-disc text-[14px] text-system-gray-200">
                  {forecast.questions.map(q => (
                    <li key={q.question}>{q.question}</li>
                  ))}
                </ul>
              </div>
            )}
            {forecast.aspects.length > 0 && (
              <div>
                <SectionTitle>What people will talk about</SectionTitle>
                <p className="mt-1 text-[14px] text-system-gray-200">
                  {forecast.aspects.map(a => aspectLabel(a.aspect)).join(", ")}
                </p>
              </div>
            )}
            {forecast.ai?.prepareReplies.length ? (
              <div className="md:col-span-2">
                <SectionTitle>Have ready</SectionTitle>
                <ul className="mt-1 flex flex-col gap-1 text-[14px]">
                  {forecast.ai.prepareReplies.map(r => (
                    <li key={r.to}>
                      <div className="text-system-gray-500">To: {r.to}</div>
                      <div className="text-system-gray-100">{r.reply}</div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </section>

        {/* Similar creatives, one by one */}
        {current && (
          <section className="flex flex-col gap-system-sm rounded-lg border border-white/10 bg-white/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-[15px] font-medium">
                Similar creative {index + 1} of {evidence.length} ·{" "}
                <span className="text-system-gray-400">
                  {Math.round(current.similarity * 100)}% similar
                </span>
              </h4>
              <div className="flex items-center gap-1">
                <Button
                  size="xs"
                  variant={{ variant: "text" }}
                  disabled={index === 0}
                  onClick={() => setIndex(i => i - 1)}
                >
                  ‹ Prev
                </Button>
                <Button
                  size="xs"
                  variant={{ variant: "text" }}
                  disabled={index >= evidence.length - 1}
                  onClick={() => setIndex(i => i + 1)}
                >
                  Next ›
                </Button>
              </div>
            </div>
            {current.verification && (
              <div className="flex flex-col gap-1 rounded-md border border-white/5 bg-white/[0.03] p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[12px] ${MATCH_BADGE[current.verification.match].cls}`}
                  >
                    {MATCH_BADGE[current.verification.match].label}
                  </span>
                  <span className="text-[12px] text-system-gray-500">
                    AI style match{" "}
                    {Math.round(current.verification.score * 100)}% · weight{" "}
                    {Math.round(current.weight * 100)}%
                  </span>
                </div>
                <p className="text-[14px] text-system-gray-100">
                  {current.verification.reason}
                </p>
                {current.verification.sharedTraits.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 text-[12px]">
                    <span className="text-system-gray-500">Shared:</span>
                    {current.verification.sharedTraits.map(t => (
                      <span
                        key={t}
                        className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-emerald-200"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                {current.verification.differences.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 text-[12px]">
                    <span className="text-system-gray-500">Differs:</span>
                    {current.verification.differences.map(t => (
                      <span
                        key={t}
                        className="rounded-full bg-rose-500/10 px-2 py-0.5 text-rose-200"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {current.snippet && (
              <p className="line-clamp-3 text-[14px] text-system-gray-400">
                {current.snippet}
              </p>
            )}
            <div className="flex flex-col gap-system-sm">
              {current.ads.map(ad => (
                <div
                  key={ad.adId}
                  className="flex gap-system-sm rounded-md border border-white/5 bg-white/[0.03] p-3"
                >
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md bg-white/5">
                    <MarathonImage
                      sourceType="FACEBOOK_AD"
                      externalRefId={ad.adId}
                      alt={ad.adName ?? ad.adId}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <div className="flex items-center gap-3">
                      <span className="min-w-0 flex-1 truncate text-[14px] text-system-gray-100">
                        {ad.adName ?? `Ad ${ad.adId}`}
                      </span>
                      {ad.reaction && (
                        <>
                          <span className="text-[12px] text-system-gray-500">
                            {ad.reaction.comments} comment
                            {ad.reaction.comments === 1 ? "" : "s"}
                            {ad.reaction.hidden
                              ? ` · ${ad.reaction.hidden} hidden`
                              : ""}
                          </span>
                          <ScoreBadge score={ad.reaction.score} />
                        </>
                      )}
                    </div>
                    {ad.reaction && (
                      <StanceBar
                        counts={ad.reaction}
                        title={stanceTitle(ad.reaction)}
                      />
                    )}
                    {ad.summary && (
                      <p className="text-[14px] text-system-gray-200">
                        {ad.summary.headline}
                      </p>
                    )}
                    <div className="grid grid-cols-1 gap-system-sm md:grid-cols-3">
                      {ad.reaction && (
                        <>
                          <QuoteList
                            label="Positive"
                            quotes={ad.reaction.quotes.positive}
                            cls="text-emerald-300"
                            size="md"
                          />
                          <QuoteList
                            label="Questions"
                            quotes={ad.reaction.quotes.question}
                            cls="text-sky-300"
                            size="md"
                          />
                          <QuoteList
                            label="Negative"
                            quotes={ad.reaction.quotes.negative}
                            cls="text-rose-300"
                            size="md"
                          />
                        </>
                      )}
                    </div>
                    {ad.summary && ad.summary.body.objections.length > 0 && (
                      <div>
                        <SectionTitle>Pushback</SectionTitle>
                        <ul className="mt-1 flex flex-col gap-0.5 text-[14px] text-system-gray-200">
                          {ad.summary.body.objections.slice(0, 4).map(o => (
                            <li key={o.claim}>
                              <span className="font-mono text-system-gray-500">
                                {o.mentions}×
                              </span>{" "}
                              {o.claim}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </Dialog>
  )
}
